// Typed wrapper around the agent's mutate endpoints.
//
// PR 2 of `auth-via-telegram-otp` rewires this module: the previous
// per-action TOTP gate (`X-2FA-Token` header) is REMOVED. Mutates now
// authenticate with the session cookie (browser auto-sends
// `dashboard_session=…`) plus the `X-CSRF-Token` header pulled from
// the in-memory auth store (`authUser.csrf`).
//
// Contract reference:
//
//   POST /api/restart/{container}    — body { action }  → { ok: true }
//   POST /api/stop/{container}       — body { action }  → { ok: true }
//   POST /api/start/{container}      — body { action }  → { ok: true }
//   POST /api/bulk                   — body { action, containers }
//                                     → BulkMutateResponse (cap = 5)
//
// Headers attached on every mutating call:
//   X-CSRF-Token    : from `authUser.csrf` (the agent rejects 419
//                     when this is missing or stale).
//   Content-Type    : application/json
//   (Cookie)        : dashboard_session=<id> from the browser's
//                     cookie jar; no `X-2FA-Token` / `X-Dev-User` —
//                     both paths are gone in PR 2.
//
// Errors:
//   - 401 / 419 / 429 → MutateError with the status; the caller
//     surfaces the appropriate user-facing copy.
//   - Bulk entry > 5 → MutateError, thrown client-side, no fetch fired.
//   - Bulk entry errors per-container → returned on the response, NOT
//     thrown (the page renders partial success).

import { csrfHeader, authUser, getValue, ApiError } from "./api";
import type {
  BulkMutateResponse,
  BulkStatus,
  MutateAction,
} from "./bindings";

/** Maximum number of containers a single bulk action may include. */
export const BULK_CAP = 5;

export class MutateError extends Error {
  readonly status: number;
  readonly code: string;
  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = "MutateError";
    this.status = status;
    this.code = code;
  }
}

// --- shared internal helper ----------------------------------------------

interface SingleMutateOptions {
  container: string;
  /** @deprecated — TOTP removed in PR 2. Accepted for the
   *  `ActionModal` legacy call site; ignored on the wire. */
  totp?: string;
}

interface BulkMutateOptions {
  action: MutateAction;
  containers: string[];
  /** @deprecated — TOTP removed in PR 2. */
  totp?: string;
}

/** Single mutate — POST `/api/{action}/{container}` with the CSRF
 *  header; the cookie rides along on the browser's cookie jar. */
async function singleMutate(
  action: MutateAction,
  opts: SingleMutateOptions,
): Promise<{ ok: boolean }> {
  // localStorage might not have been hydrated yet — `authUser` reads
  // straight from the in-memory store, set when `verifyOtp` succeeded.
  const csrf = getValue(authUser)?.csrf;
  if (!csrf) {
    throw new MutateError(401, "no-session", "no active session");
  }
  void opts.totp; // legacy field — present for ActionModal compat.
  const url = `/api/${action}/${encodeURIComponent(opts.container)}`;
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...csrfHeader(),
    },
    body: JSON.stringify({ action }),
  });
  if (!res.ok) {
    throw asMutateError(res.status, await safeBody(res));
  }
  return (await res.json()) as { ok: boolean };
}

async function bulkMutatePost(opts: BulkMutateOptions): Promise<BulkMutateResponse> {
  if (opts.containers.length > BULK_CAP) {
    throw new MutateError(
      400,
      "bulk-cap-exceeded",
      `Bulk action limited to ${BULK_CAP} containers`,
    );
  }
  const csrf = getValue(authUser)?.csrf;
  if (!csrf) {
    throw new MutateError(401, "no-session", "no active session");
  }
  void opts.totp;
  const url = "/api/bulk";
  const res = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...csrfHeader(),
    },
    body: JSON.stringify({
      action: opts.action,
      containers: opts.containers,
    }),
  });
  if (!res.ok) {
    throw asMutateError(res.status, await safeBody(res));
  }
  return (await res.json()) as BulkMutateResponse;
}

// --- public API ----------------------------------------------------------

export function startContainer(opts: SingleMutateOptions): Promise<{ ok: boolean }> {
  return singleMutate("start", opts);
}

export function stopContainer(opts: SingleMutateOptions): Promise<{ ok: boolean }> {
  return singleMutate("stop", opts);
}

export function restartContainer(opts: SingleMutateOptions): Promise<{ ok: boolean }> {
  return singleMutate("restart", opts);
}

export function bulkMutate(opts: BulkMutateOptions): Promise<BulkMutateResponse> {
  return bulkMutatePost(opts);
}

// --- response helpers ----------------------------------------------------

async function safeBody(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return "";
  }
}

function asMutateError(status: number, body: string): MutateError {
  let code = "mutate-failed";
  let message = body || `mutate failed (${status})`;
  try {
    const parsed = JSON.parse(body) as { error?: string; code?: string };
    if (parsed.code) code = parsed.code;
    else if (parsed.error) code = parsed.error;
    if (parsed.error) message = parsed.error;
  } catch {
    // Body wasn't JSON — keep the raw text in the message.
  }
  return new MutateError(status, code, message);
}

// Suppress unused import lints — `ApiError`/`BulkStatus` are
// re-exported so the legacy call sites keep working without changes.
export { ApiError };
export type { BulkStatus };